import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { personKeys } from "@/lib/query";

export interface AspirationRole {
  /** The entry's own id — its identity for level / remove / reorder calls. */
  id: string;
  roleId: string;
  roleName: string;
  roleStatus: string;
  /** The career level being worked toward — null when none was named. */
  roleLevelId: string | null;
  roleLevelName: string | null;
  /** Priority within the person's list — 1 = first choice. */
  rank: number;
}

interface AspirationsPayload {
  aspirations: AspirationRole[];
  /** True when the viewer (admin or the person themselves) may add/remove flags. */
  canManage: boolean;
}

/**
 * A person's aspirational roles. The endpoint 403s when the viewer isn't
 * allowed to see them (not admin / self / their manager) — callers hide the
 * section on `isError` rather than surfacing an error. `retry: false` so a 403
 * doesn't thrash.
 */
export function usePersonAspirations(personId: string) {
  return useQuery({
    queryKey: personKeys.aspirations(personId),
    retry: false,
    queryFn: async (): Promise<AspirationsPayload> => {
      const res = await fetch(`/api/persons/${personId}/aspirations`);
      if (!res.ok) throw new Error("forbidden");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface AspirationReport {
  scope: "admin" | "manager" | "none";
  byPerson: {
    personId: string;
    personName: string;
    /** In the person's priority order (rank 1 first). */
    roles: {
      id: string;
      roleId: string;
      roleName: string;
      roleLevelName: string | null;
      rank: number;
    }[];
  }[];
  byRole: {
    roleId: string;
    roleName: string;
    /** rank = where this role sits in that person's own priority list. */
    people: {
      id: string;
      personId: string;
      personName: string;
      roleLevelName: string | null;
      rank: number;
    }[];
  }[];
}

/** The scoped manager/admin report (admins = everyone, managers = their reports). */
export function useAspirationReport() {
  return useQuery({
    queryKey: personKeys.aspirationReport,
    queryFn: async (): Promise<AspirationReport> => {
      const res = await fetch("/api/aspirations/report");
      if (!res.ok) throw new Error("Failed to load aspirations report");
      const json = await res.json();
      return json.data;
    },
  });
}

/**
 * Flag a role, optionally at a target career level. The same role may be flagged
 * at several levels (Associate now, Staff later); the identical pair is refused
 * by the server with a message worth surfacing verbatim.
 */
export function useAddPersonAspiration(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      roleId,
      roleLevelId,
    }: {
      roleId: string;
      roleLevelId?: string | null;
    }) => {
      const res = await fetch(`/api/persons/${personId}/aspirations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roleId, roleLevelId: roleLevelId ?? null }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to add role");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.aspirations(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.aspirationReport });
      // Surfaces in the person's Change history.
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      toast.success("Role flagged");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to add role"),
  });
}

/**
 * Rewrite the priority order — pass every entry's id in the new order. Not role
 * ids: the same role can be listed at two levels.
 */
export function useReorderPersonAspirations(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (aspirationIds: string[]) => {
      const res = await fetch(`/api/persons/${personId}/aspirations`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aspirationIds }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to reorder");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.aspirations(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.aspirationReport });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to reorder"),
  });
}

/** Remove one entry, by its own id (a role id could match two entries). */
export function useRemovePersonAspiration(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (aspirationId: string) => {
      const res = await fetch(`/api/persons/${personId}/aspirations/${aspirationId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to remove role");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.aspirations(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.aspirationReport });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      toast.success("Role removed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to remove role"),
  });
}

/**
 * Retarget one listed entry's career level (null = back to any level). Separate
 * from adding, which now always creates a new entry — the same role at a
 * different rung is a legitimate second aspiration, so a POST can't double as
 * "change this one".
 */
export function useSetPersonAspirationLevel(personId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      aspirationId,
      roleLevelId,
    }: {
      aspirationId: string;
      roleLevelId: string | null;
    }) => {
      const res = await fetch(`/api/persons/${personId}/aspirations/${aspirationId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roleLevelId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error ?? "Failed to set the level");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: personKeys.aspirations(personId) });
      queryClient.invalidateQueries({ queryKey: personKeys.aspirationReport });
      queryClient.invalidateQueries({ queryKey: personKeys.detail(personId) });
      toast.success("Level updated");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to set the level"),
  });
}
